<!-- eslint-disable vue/no-bare-strings-in-template -->
<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import WahaAPI from 'dashboard/api/wahaIntegrations';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const loading = ref(false);
const showAdvancedOptions = ref(false);

// Valores por defecto del .env
const DEFAULT_WAHA_SERVER_URL = 'https://apiwaha2.tulogica.com';
const DEFAULT_WAHA_API_KEY = 'ASDTAEFANSFLKAHDD23AQSEF23F';
const DEFAULT_CHATWOOT_URL = 'http://crm_chatwoot:3000';

const wahaServerUrlFromEnv = computed(
  () => window.chatwootConfig?.WAHA_SERVER_URL || DEFAULT_WAHA_SERVER_URL
);
const wahaApiKeyFromEnv = computed(
  () => window.chatwootConfig?.WAHA_API_KEY || DEFAULT_WAHA_API_KEY
);
const chatwootUrlFromEnv = computed(
  () => window.chatwootConfig?.CHATWOOT_PUBLIC_URL || DEFAULT_CHATWOOT_URL
);

const formData = ref({
  inboxName: 'WhatsApp QR',
  deviceName: 'VENTAS',
  wahaServerUrl:
    window.chatwootConfig?.WAHA_SERVER_URL || DEFAULT_WAHA_SERVER_URL,
  wahaApiKey: window.chatwootConfig?.WAHA_API_KEY || DEFAULT_WAHA_API_KEY,
  chatwootUrl:
    window.chatwootConfig?.CHATWOOT_PUBLIC_URL || DEFAULT_CHATWOOT_URL,
});

const createIntegration = async () => {
  // Validar que haya valores (ya sea del form o del .env)
  if (!formData.value.wahaServerUrl) {
    useAlert(
      t('INBOX_MGMT.ADD.WAHA.ERRORS.SERVER_URL_REQUIRED') ||
        'Por favor configura la URL del servidor WAHA en .env o ingresa una'
    );
    return;
  }

  if (!formData.value.wahaApiKey) {
    useAlert(
      t('INBOX_MGMT.ADD.WAHA.ERRORS.API_KEY_REQUIRED') ||
        'Por favor configura la API Key de WAHA en .env o ingresa una'
    );
    return;
  }

  if (!formData.value.chatwootUrl) {
    useAlert(
      t('INBOX_MGMT.ADD.WAHA.ERRORS.CHATWOOT_URL_REQUIRED') ||
        'Por favor configura la URL de Chatwoot en .env o ingresa una'
    );
    return;
  }

  try {
    loading.value = true;
    const response = await WahaAPI.create(formData.value);

    if (response.data.success) {
      const inboxId = response.data.inbox.id;

      useAlert(
        t('INBOX_MGMT.ADD.WAHA.SUCCESS.CREATED') ||
          'Integración WAHA creada exitosamente. Redirigiendo a configuración...'
      );

      // Redirigir a la configuración del inbox donde se puede escanear el QR
      setTimeout(() => {
        router.push({
          name: 'settings_inbox_show',
          params: { accountId: accountId.value, inboxId },
        });
      }, 1500);
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      t('INBOX_MGMT.ADD.WAHA.ERRORS.CREATE_FAILED') ||
      'Error al crear la integración WAHA';
    useAlert(errorMessage);
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.push({
    name: 'settings_inbox_new',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <div class="flex flex-col h-full">
    <div
      class="flex items-center justify-between p-6 border-b border-slate-75 dark:border-slate-700"
    >
      <div>
        <h2 class="text-2xl font-semibold text-slate-900 dark:text-slate-25">
          {{ $t('INBOX_MGMT.ADD.WAHA.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ADD.WAHA.DESC') }}
        </p>
      </div>
    </div>

    <div class="flex-1 p-6 overflow-y-auto">
      <div class="max-w-2xl mx-auto">
        <!-- Formulario -->
        <form class="space-y-6" @submit.prevent="createIntegration">
          <div>
            <label
              class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ $t('INBOX_MGMT.ADD.WAHA.INBOX_NAME.LABEL') }}
              {{ $t('GENERAL.REQUIRED') || '*' }}
            </label>
            <input
              v-model="formData.inboxName"
              type="text"
              required
              :placeholder="$t('INBOX_MGMT.ADD.WAHA.INBOX_NAME.PLACEHOLDER')"
              class="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            />
            <p class="mt-1.5 text-xs text-slate-500">
              {{ $t('INBOX_MGMT.ADD.WAHA.INBOX_NAME.SUBTITLE') }}
            </p>
          </div>

          <div>
            <label
              class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ $t('INBOX_MGMT.ADD.WAHA.DEVICE_NAME.LABEL') }}
            </label>
            <input
              v-model="formData.deviceName"
              type="text"
              :placeholder="$t('INBOX_MGMT.ADD.WAHA.DEVICE_NAME.PLACEHOLDER')"
              class="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            />
            <p class="mt-1.5 text-xs text-slate-500">
              {{ $t('INBOX_MGMT.ADD.WAHA.DEVICE_NAME.SUBTITLE') }}
            </p>
          </div>

          <!-- Botón Opciones Avanzadas -->
          <div class="border-t border-slate-200 dark:border-slate-700 pt-4">
            <button
              type="button"
              class="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
              @click="showAdvancedOptions = !showAdvancedOptions"
            >
              <svg
                class="w-4 h-4 transition-transform"
                :class="{ 'rotate-90': showAdvancedOptions }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span>{{
                showAdvancedOptions
                  ? $t('INBOX_MGMT.ADD.CHANNEL_NAME.HIDE_ADVANCED') ||
                    'Ocultar opciones avanzadas'
                  : $t('INBOX_MGMT.ADD.CHANNEL_NAME.SHOW_ADVANCED') ||
                    'Mostrar opciones avanzadas'
              }}</span>
            </button>
            <p class="mt-1 ml-6 text-xs text-slate-500">
              {{ $t('INBOX_MGMT.ADD.WAHA.INBOX_NAME.SUBTITLE') }}
            </p>
          </div>

          <!-- Campos Avanzados (ocultos por defecto) -->
          <div
            v-if="showAdvancedOptions"
            class="space-y-6 pl-4 border-l-2 border-slate-200 dark:border-slate-700"
          >
            <!-- Información importante movida aquí -->
            <div
              class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
            >
              <h3
                class="mb-2 text-sm font-medium text-blue-900 dark:text-blue-100"
              >
                {{ $t('INBOX_MGMT.ADD.WAHA.BEFORE_START.TITLE') }}
              </h3>
              <ul
                class="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside"
              >
                <li>{{ $t('INBOX_MGMT.ADD.WAHA.BEFORE_START.ITEM_1') }}</li>
                <li>{{ $t('INBOX_MGMT.ADD.WAHA.BEFORE_START.ITEM_2') }}</li>
                <li>
                  {{ $t('INBOX_MGMT.ADD.WAHA.BEFORE_START.ITEM_3') }}
                </li>
                <li>{{ $t('INBOX_MGMT.ADD.WAHA.BEFORE_START.ITEM_4') }}</li>
              </ul>
            </div>

            <div>
              <label
                class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                {{ $t('INBOX_MGMT.ADD.WAHA.WAHA_SERVER_URL.LABEL') }}
              </label>
              <input
                v-model="formData.wahaServerUrl"
                type="url"
                :placeholder="
                  $t('INBOX_MGMT.ADD.WAHA.WAHA_SERVER_URL.PLACEHOLDER') ||
                  'https://apiwaha.tulogica.com'
                "
                class="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
              />
              <p class="mt-1.5 text-xs text-slate-500">
                {{ $t('INBOX_MGMT.ADD.WAHA.WAHA_SERVER_URL.SUBTITLE') + ':' }}
                {{
                  wahaServerUrlFromEnv ||
                  $t('GENERAL.NOT_CONFIGURED') ||
                  'No configurado'
                }}
              </p>
            </div>

            <div>
              <label
                class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                {{ $t('INBOX_MGMT.ADD.WAHA.WAHA_API_KEY.LABEL') }}
              </label>
              <input
                v-model="formData.wahaApiKey"
                type="password"
                :placeholder="
                  $t('INBOX_MGMT.ADD.WAHA.WAHA_API_KEY.PLACEHOLDER') ||
                  'Tu API Key de WAHA'
                "
                class="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
              />
              <p class="mt-1.5 text-xs text-slate-500">
                {{
                  wahaApiKeyFromEnv
                    ? $t('INBOX_MGMT.ADD.WAHA.WAHA_API_KEY.SUBTITLE')
                    : $t('INBOX_MGMT.ADD.WAHA.WAHA_API_KEY.ENTER_KEY') ||
                      'Ingresa tu API Key de WAHA'
                }}
              </p>
            </div>

            <div>
              <label
                class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                {{ $t('INBOX_MGMT.ADD.WAHA.CHATWOOT_URL.LABEL') }}
              </label>
              <input
                v-model="formData.chatwootUrl"
                type="url"
                :placeholder="
                  $t('INBOX_MGMT.ADD.WAHA.CHATWOOT_URL.PLACEHOLDER') ||
                  'https://tu-chatwoot.com'
                "
                class="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
              />
              <p class="mt-1.5 text-xs text-slate-500">
                {{
                  chatwootUrlFromEnv
                    ? `${$t('INBOX_MGMT.ADD.WAHA.CHATWOOT_URL.SUBTITLE')}: ${chatwootUrlFromEnv}`
                    : $t('INBOX_MGMT.ADD.WAHA.CHATWOOT_URL.ENTER_URL') ||
                      'Ingresa la URL de Chatwoot'
                }}
              </p>
            </div>
          </div>

          <!-- Qué se creará -->
          <div
            class="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
          >
            <h4
              class="mb-2 text-sm font-medium text-green-900 dark:text-green-100"
            >
              {{ $t('INBOX_MGMT.ADD.WAHA.AUTO_CREATE.TITLE') }}
            </h4>
            <ul
              class="text-sm text-green-800 dark:text-green-200 space-y-1 list-disc list-inside"
            >
              <li>{{ $t('INBOX_MGMT.ADD.WAHA.AUTO_CREATE.ITEM_1') }}</li>
              <li>{{ $t('INBOX_MGMT.ADD.WAHA.AUTO_CREATE.ITEM_2') }}</li>
              <li>{{ $t('INBOX_MGMT.ADD.WAHA.AUTO_CREATE.ITEM_3') }}</li>
              <li>{{ $t('INBOX_MGMT.ADD.WAHA.AUTO_CREATE.ITEM_4') }}</li>
            </ul>
          </div>

          <!-- Botones -->
          <div class="flex gap-3 pt-4">
            <button
              type="submit"
              :disabled="loading"
              class="px-6 py-2.5 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span v-if="loading" class="flex items-center gap-2">
                <svg
                  class="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {{
                  $t('INBOX_MGMT.ADD.WAHA.BUTTONS.CREATING') ||
                  'Creando integración...'
                }}
              </span>
              <span v-else>{{
                $t('INBOX_MGMT.ADD.WAHA.BUTTONS.CREATE') ||
                'Crear Integración WAHA'
              }}</span>
            </button>
            <button
              type="button"
              :disabled="loading"
              class="px-6 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
              @click="goBack"
            >
              {{ $t('GENERAL.CANCEL') || 'Cancelar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
